import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPowerOff, faUserPlus, faSignInAlt, faGaugeHigh, faChevronDown, faBars, faXmark, faMoon, faSun, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG } from '../config/api';

function Navbar({ isAuthenticated, setIsAuthenticated }) {
    const navigate = useNavigate();
    const location = useLocation();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
    })();
    const isAdmin = currentUser?.role === 'admin';

    const handleLogout = () => {
        // Ukloni token iz lokalne pohrane
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Ažuriraj stanje prijave
        setIsAuthenticated(false);
        // Preusmjeri korisnika na stranicu za prijavu
        navigate('/login');
    };

    // Theme state
    const [theme, setTheme] = useState(() => {
        try { return localStorage.getItem('theme') || 'light'; } catch { return 'light'; }
    });
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        try { localStorage.setItem('theme', theme); } catch {}
    }, [theme]);
    const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

    // Mobile menu state
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileSubmenu, setMobileSubmenu] = useState(null);
    const toggleMobileSubmenu = (menu) => setMobileSubmenu((prev) => (prev === menu ? null : menu));

    // Desktop dropdown state management
    const [openMenu, setOpenMenu] = useState(null);
    const closeTimerRef = useRef(null);

    const openDropdown = (menuName) => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setOpenMenu(menuName);
    };

    const scheduleCloseDropdown = () => {
        closeTimerRef.current = setTimeout(() => {
            setOpenMenu(null);
            closeTimerRef.current = null;
        }, 300);
    };

    const cancelCloseDropdown = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    // Company name and logo for brand title
    const [companyName, setCompanyName] = useState('Nalozi');
    const [navbarLogo, setNavbarLogo] = useState(null);
    const [registrationEnabled, setRegistrationEnabled] = useState(false);
    
    const loadCompanyInfo = async () => {
        try {
            const res = await fetch(API_CONFIG.ENDPOINTS.COMPANY_INFO);
            if (!res.ok) return;
            const data = await res.json();
            if (data?.name) setCompanyName(data.name);
            setNavbarLogo(data?.navbar_logo_url || null);
            setRegistrationEnabled(!!data?.registration_enabled);
        } catch {}
    };
    
    useEffect(() => {
        loadCompanyInfo();
        
        // Listen for navbar logo updates
        const handleNavbarLogoUpdate = () => {
            loadCompanyInfo();
        };
        
        window.addEventListener('navbarLogoUpdated', handleNavbarLogoUpdate);
        
        return () => {
            window.removeEventListener('navbarLogoUpdated', handleNavbarLogoUpdate);
        };
    }, []);

    // Active route helpers
    const path = location.pathname || '';
    const startsWith = (prefixes) => prefixes.some((p) => path.startsWith(p));
    const activeHome = path === '/dashboard' || path === '/';
    const activeNalozi = startsWith(['/dashboard', '/new-work-order', '/work-orders']);
    const activeUstanove = startsWith(['/ustanove', '/new-ustanova']);
    const activeAparati = startsWith(['/aparati', '/new-aparat']);
    const activeDijelovi = startsWith(['/dijelovi', '/new-dio']);
    const activeReports = startsWith(['/reports']);
    const activeAdmin = startsWith(['/users', '/audit-logs', '/admin/company']);
    const activeProfile = startsWith(['/profile']);

    const topLinkClass = (active) => active ? 'text-gray-900 font-semibold' : 'text-gray-700 hover:text-gray-900';
    const groupTriggerClass = (active) => `flex items-center ${active ? 'text-gray-900 font-semibold' : 'text-gray-700'} hover:text-gray-900 cursor-pointer`;
    const submenuLinkClass = (active) => `block px-4 py-2 text-sm ${active ? 'text-gray-900 bg-gray-100' : 'text-gray-700 hover:bg-gray-50'}`;
    const mobileTitleClass = (active) => `w-full text-left flex items-center justify-between ${active ? 'text-gray-900 font-semibold' : 'text-gray-700'} hover:text-gray-900 py-2`;
    const mobileLinkClass = (active) => `block ${active ? 'text-gray-900 font-semibold' : 'text-gray-700 hover:text-gray-900'}`;

    return (
        <nav className="bg-white/80 backdrop-blur border-b border-gray-200">
            <div className="container mx-auto px-4 py-3 flex justify-between items-center">
                <Link to="/dashboard" className="text-xl font-semibold text-gray-900 flex items-center">
                    {navbarLogo ? (
                        <img 
                            src={navbarLogo} 
                            alt="Navbar Logo" 
                            className="mr-2 h-8 w-8 object-contain"
                            onError={() => setNavbarLogo(null)}
                        />
                    ) : (
                        <FontAwesomeIcon icon={faGaugeHigh} className="mr-2 text-blue-600" />
                    )}
                    {companyName || 'Panel'} Panel
                </Link>
                <button
                    className="sm:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100"
                    aria-label="Otvori meni"
                    onClick={() => setMobileOpen((v) => !v)}
                >
                    <FontAwesomeIcon icon={mobileOpen ? faXmark : faBars} className="text-xl" />
                </button>
                {/* Uklonjeno mobilno dugme za promjenu teme iz navbar trake */}
                <div className="hidden sm:flex items-center space-x-4">
                    {isAuthenticated ? (
                        <>
                            <Link to="/dashboard" className={topLinkClass(activeHome)}>Početna</Link>

                            {/* Nalozi Dropdown */}
                            <div className="relative">
                                <div 
                                    className={groupTriggerClass(activeNalozi)}
                                    onClick={() => openDropdown(openMenu === 'nalozi' ? null : 'nalozi')}
                                    onMouseEnter={() => openDropdown('nalozi')}
                                    onMouseLeave={scheduleCloseDropdown}
                                >
                                    Nalozi
                                    <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
                                </div>
                                {openMenu === 'nalozi' && (
                                    <div 
                                        className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                                        onMouseEnter={cancelCloseDropdown}
                                        onMouseLeave={scheduleCloseDropdown}
                                    >
                                        <Link to="/dashboard" className={submenuLinkClass(path === '/dashboard')}>Lista naloga</Link>
                                        {currentUser?.role !== 'finance' && (
                                            <Link to="/new-work-order" className={submenuLinkClass(path.startsWith('/new-work-order'))}>Novi nalog</Link>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Ustanove Dropdown */}
                            <div className="relative">
                                <div 
                                    className={groupTriggerClass(activeUstanove)}
                                    onClick={() => openDropdown(openMenu === 'ustanove' ? null : 'ustanove')}
                                    onMouseEnter={() => openDropdown('ustanove')}
                                    onMouseLeave={scheduleCloseDropdown}
                                >
                                    Ustanove
                                    <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
                                </div>
                                {openMenu === 'ustanove' && (
                                    <div 
                                        className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                                        onMouseEnter={cancelCloseDropdown}
                                        onMouseLeave={scheduleCloseDropdown}
                                    >
                                        <Link to="/ustanove" className={submenuLinkClass(path.startsWith('/ustanove'))}>Lista ustanova</Link>
                                        {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                                            <Link to="/new-ustanova" className={submenuLinkClass(path.startsWith('/new-ustanova'))}>Dodaj novu ustanovu</Link>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Aparati Dropdown */}
                            <div className="relative">
                                <div 
                                    className={groupTriggerClass(activeAparati)}
                                    onClick={() => openDropdown(openMenu === 'aparati' ? null : 'aparati')}
                                    onMouseEnter={() => openDropdown('aparati')}
                                    onMouseLeave={scheduleCloseDropdown}
                                >
                                    Aparati
                                    <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
                                </div>
                                {openMenu === 'aparati' && (
                                    <div 
                                        className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                                        onMouseEnter={cancelCloseDropdown}
                                        onMouseLeave={scheduleCloseDropdown}
                                    >
                                        <Link to="/aparati" className={submenuLinkClass(path.startsWith('/aparati'))}>Lista aparata</Link>
                                        {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                                            <Link to="/new-aparat" className={submenuLinkClass(path.startsWith('/new-aparat'))}>Dodaj novi aparat</Link>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Dijelovi Dropdown */}
                            <div className="relative">
                                <div 
                                    className={groupTriggerClass(activeDijelovi)}
                                    onClick={() => openDropdown(openMenu === 'dijelovi' ? null : 'dijelovi')}
                                    onMouseEnter={() => openDropdown('dijelovi')}
                                    onMouseLeave={scheduleCloseDropdown}
                                >
                                    Dijelovi
                                    <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
                                </div>
                                {openMenu === 'dijelovi' && (
                                    <div 
                                        className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                                        onMouseEnter={cancelCloseDropdown}
                                        onMouseLeave={scheduleCloseDropdown}
                                    >
                                        <Link to="/dijelovi" className={submenuLinkClass(path.startsWith('/dijelovi') && !path.startsWith('/dijelovi/low-stock') && !path.startsWith('/new-dio'))}>Lista dijelova</Link>
                                        {(currentUser?.role === 'admin' || currentUser?.role === 'finance') && (
                                            <Link to="/dijelovi/low-stock" className={submenuLinkClass(path.startsWith('/dijelovi/low-stock'))}>Nedostatak dijelova</Link>
                                        )}
                                        {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                                            <Link to="/new-dio" className={submenuLinkClass(path.startsWith('/new-dio'))}>Dodaj novi dio</Link>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Izvještaji Dropdown */}
                            {(currentUser?.role === 'admin' || currentUser?.role === 'finance') && (
                                <div className="relative">
                                    <div 
                                        className={groupTriggerClass(activeReports)}
                                        onClick={() => openDropdown(openMenu === 'izvjestaji' ? null : 'izvjestaji')}
                                        onMouseEnter={() => openDropdown('izvjestaji')}
                                        onMouseLeave={scheduleCloseDropdown}
                                    >
                                        Izvještaji
                                        <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
                                    </div>
                                    {openMenu === 'izvjestaji' && (
                                        <div 
                                            className="absolute right-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                                            onMouseEnter={cancelCloseDropdown}
                                            onMouseLeave={scheduleCloseDropdown}
                                        >
                                            <Link to="/reports/services-per-aparat" className={submenuLinkClass(path.startsWith('/reports/services-per-aparat'))}>Broj servisa po aparatu</Link>
                                            <Link to="/reports/services-per-serviser" className={submenuLinkClass(path.startsWith('/reports/services-per-serviser'))}>Broj servisa po serviserima</Link>
                                            <Link to="/reports/services-per-ustanova" className={submenuLinkClass(path.startsWith('/reports/services-per-ustanova'))}>Broj servisa po ustanovama</Link>
                                            <Link to="/reports/parts-per-aparat" className={submenuLinkClass(path.startsWith('/reports/parts-per-aparat'))}>Utrošeni dijelovi po aparatima</Link>
                                            <Link to="/reports/consumed-parts" className={submenuLinkClass(path.startsWith('/reports/consumed-parts'))}>Utrošeni dijelovi</Link>
                                            <Link to="/reports/time-per-aparat-serviser" className={submenuLinkClass(path.startsWith('/reports/time-per-aparat-serviser'))}>Utrošeno vrijeme po aparatu/serviseru</Link>
                                            <Link to="/reports/time-per-serviser" className={submenuLinkClass(path.startsWith('/reports/time-per-serviser'))}>Utrošeno vrijeme po serviseru</Link>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Admin Panel Dropdown */}
                            {isAdmin && (
                                <div className="relative">
                                    <div 
                                        className={groupTriggerClass(activeAdmin)}
                                        onClick={() => openDropdown(openMenu === 'admin' ? null : 'admin')}
                                        onMouseEnter={() => openDropdown('admin')}
                                        onMouseLeave={scheduleCloseDropdown}
                                    >
                                        Admin Panel
                                        <FontAwesomeIcon icon={faUserShield} className="ml-2 text-rose-600" title="Admin" />
                                        <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
                                    </div>
                                    {openMenu === 'admin' && (
                                        <div 
                                            className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                                            onMouseEnter={cancelCloseDropdown}
                                            onMouseLeave={scheduleCloseDropdown}
                                        >
                                            <Link to="/users" className={submenuLinkClass(path === '/users')}>Lista korisnika</Link>
                                            <Link to="/users/new" className={submenuLinkClass(path === '/users/new')}>Dodaj korisnika</Link>
                                            <Link to="/audit-logs" className={submenuLinkClass(path === '/audit-logs')}>Audit logovi</Link>
                                            <Link to="/admin/company" className={submenuLinkClass(path === '/admin/company')}>O firmi</Link>
                                        </div>
                                    )}
                                </div>
                            )}

                            <Link to="/profile" className={topLinkClass(activeProfile)}>Moj profil</Link>

                            <button
                                onClick={handleLogout}
                                className="flex items-center px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                            >
                                <FontAwesomeIcon icon={faPowerOff} className="mr-2" />
                                Odjava
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="flex items-center text-gray-700 hover:text-gray-900 transition-colors">
                                <FontAwesomeIcon icon={faSignInAlt} className="mr-2" />
                                Prijava
                            </Link>
                            {registrationEnabled && (
                                <Link to="/register" className="flex items-center text-gray-700 hover:text-gray-900 transition-colors">
                                    <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                                    Registracija
                                </Link>
                            )}
                        </>
                    )}
                    <button
                        onClick={toggleTheme}
                        className="flex items-center px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                    >
                        <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} className="mr-2" />
                        {theme === 'dark' ? 'Light' : 'Dark'}
                    </button>
                </div>
            </div>
            {/* Mobile Menu */}
            {mobileOpen && (
                <div className="sm:hidden border-t border-gray-200">
                    <div className="px-4 py-3 space-y-2">
                        <button
                            onClick={toggleTheme}
                            className="w-full flex items-center justify-center px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                            aria-label="Promijeni temu"
                            title="Promijeni temu"
                        >
                            <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} className="mr-2" />
                            {theme === 'dark' ? 'Light' : 'Dark'}
                        </button>

                        {isAuthenticated ? (
                            <>
                                <Link to="/dashboard" onClick={() => { setMobileOpen(false); }} className={mobileLinkClass(activeHome)}>Početna</Link>
                                <Link to="/profile" onClick={() => { setMobileOpen(false); }} className={mobileLinkClass(activeProfile)}>Moj profil</Link>

                                <div>
                                    <button onClick={() => toggleMobileSubmenu('nalozi')} className={mobileTitleClass(activeNalozi)}>
                                        <span>Nalozi</span>
                                        <FontAwesomeIcon icon={faChevronDown} className={`ml-2 transition-transform ${mobileSubmenu === 'nalozi' ? 'rotate-180' : ''}`} />
                                    </button>
                                    {mobileSubmenu === 'nalozi' && (
                                        <div className="pl-3 space-y-2">
                                            <Link to="/dashboard" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path === '/dashboard')}>Lista naloga</Link>
                                            {currentUser?.role !== 'finance' && (
                                                <Link to="/new-work-order" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/new-work-order'))}>Novi nalog</Link>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <button onClick={() => toggleMobileSubmenu('ustanove')} className={mobileTitleClass(activeUstanove)}>
                                        <span>Ustanove</span>
                                        <FontAwesomeIcon icon={faChevronDown} className={`ml-2 transition-transform ${mobileSubmenu === 'ustanove' ? 'rotate-180' : ''}`} />
                                    </button>
                                    {mobileSubmenu === 'ustanove' && (
                                        <div className="pl-3 space-y-2">
                                            <Link to="/ustanove" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/ustanove'))}>Lista ustanova</Link>
                                            {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                                                <Link to="/new-ustanova" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/new-ustanova'))}>Dodaj novu ustanovu</Link>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <button onClick={() => toggleMobileSubmenu('aparati')} className={mobileTitleClass(activeAparati)}>
                                        <span>Aparati</span>
                                        <FontAwesomeIcon icon={faChevronDown} className={`ml-2 transition-transform ${mobileSubmenu === 'aparati' ? 'rotate-180' : ''}`} />
                                    </button>
                                    {mobileSubmenu === 'aparati' && (
                                        <div className="pl-3 space-y-2">
                                            <Link to="/aparati" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/aparati'))}>Lista aparata</Link>
                                            {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                                                <Link to="/new-aparat" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/new-aparat'))}>Dodaj novi aparat</Link>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <button onClick={() => toggleMobileSubmenu('dijelovi')} className={mobileTitleClass(activeDijelovi)}>
                                        <span>Dijelovi</span>
                                        <FontAwesomeIcon icon={faChevronDown} className={`ml-2 transition-transform ${mobileSubmenu === 'dijelovi' ? 'rotate-180' : ''}`} />
                                    </button>
                                    {mobileSubmenu === 'dijelovi' && (
                                        <div className="pl-3 space-y-2">
                                            <Link to="/dijelovi" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/dijelovi') && !path.startsWith('/dijelovi/low-stock') && !path.startsWith('/new-dio'))}>Lista dijelova</Link>
                                            {(currentUser?.role === 'admin' || currentUser?.role === 'finance') && (
                                                <Link to="/dijelovi/low-stock" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/dijelovi/low-stock'))}>Nedostatak dijelova</Link>
                                            )}
                                            {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                                                <Link to="/new-dio" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/new-dio'))}>Dodaj novi dio</Link>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {(currentUser?.role === 'admin' || currentUser?.role === 'finance') && (
                                    <div>
                                        <button onClick={() => toggleMobileSubmenu('izvjestaji')} className={mobileTitleClass(activeReports)}>
                                            <span>Izvještaji</span>
                                            <FontAwesomeIcon icon={faChevronDown} className={`ml-2 transition-transform ${mobileSubmenu === 'izvjestaji' ? 'rotate-180' : ''}`} />
                                        </button>
                                        {mobileSubmenu === 'izvjestaji' && (
                                            <div className="pl-3 space-y-2">
                                                <Link to="/reports/services-per-aparat" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/reports/services-per-aparat'))}>Broj servisa po aparatu</Link>
                                                <Link to="/reports/services-per-serviser" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/reports/services-per-serviser'))}>Broj servisa po serviserima</Link>
                                                <Link to="/reports/services-per-ustanova" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/reports/services-per-ustanova'))}>Broj servisa po ustanovama</Link>
                                                <Link to="/reports/parts-per-aparat" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/reports/parts-per-aparat'))}>Utrošeni dijelovi po aparatima</Link>
                                                <Link to="/reports/consumed-parts" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/reports/consumed-parts'))}>Utrošeni dijelovi</Link>
                                                <Link to="/reports/time-per-aparat-serviser" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/reports/time-per-aparat-serviser'))}>Utrošeno vrijeme po aparatu/serviseru</Link>
                                                <Link to="/reports/time-per-serviser" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path.startsWith('/reports/time-per-serviser'))}>Utrošeno vrijeme po serviseru</Link>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {isAdmin && (
                                    <div>
                                        <button onClick={() => toggleMobileSubmenu('korisnici')} className={mobileTitleClass(activeAdmin)}>
                                            <span className="flex items-center">Admin Panel <FontAwesomeIcon icon={faUserShield} className="ml-2 text-rose-600" title="Admin" /></span>
                                            <FontAwesomeIcon icon={faChevronDown} className={`ml-2 transition-transform ${mobileSubmenu === 'korisnici' ? 'rotate-180' : ''}`} />
                                        </button>
                                        {mobileSubmenu === 'korisnici' && (
                                            <div className="pl-3 space-y-2">
                                                <Link to="/users" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path === '/users')}>Lista korisnika</Link>
                                                <Link to="/users/new" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path === '/users/new')}>Dodaj korisnika</Link>
                                                <Link to="/audit-logs" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path === '/audit-logs')}>Audit logovi</Link>
                                                <Link to="/admin/company" onClick={() => { setMobileOpen(false); setMobileSubmenu(null); }} className={mobileLinkClass(path === '/admin/company')}>O firmi</Link>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <button
                                    onClick={() => { setMobileOpen(false); handleLogout(); }}
                                    className="w-full flex items-center justify-center px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faPowerOff} className="mr-2" />
                                    Odjava
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => { setMobileOpen(false); }} className={mobileLinkClass(path === '/login')}>Prijava</Link>
                                {registrationEnabled && (
                                    <Link to="/register" onClick={() => { setMobileOpen(false); }} className={mobileLinkClass(path === '/register')}>Registracija</Link>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
